import { SESSION_TIMEOUT_CODE } from "../../shared/session-timeout";

/**
 * #1666 — distinguish "signed out" from "session check failed".
 *
 * better-auth nulls the client session on ANY /get-session failure, so a
 * network drop or an edge 5xx used to render exactly like a sign-out and
 * bounce a signed-in user to /welcome (client-side residue of the #1653
 * chain). A genuine sign-out is a clean null session (no error) or a 401;
 * anything else is a failed CHECK and deserves a holding state + retry, never
 * a redirect.
 *
 * Pure and defensive about the error shape: an error with no readable status
 * counts as a failed check — when we cannot tell, holding a possibly-valid
 * session beats dumping a possibly-signed-in user to the landing page.
 */
export function sessionCheckFailed(hasUser: boolean, error: unknown): boolean {
  if (hasUser || error == null) return false;
  const status =
    (error as { status?: unknown }).status ??
    (error as { statusCode?: unknown }).statusCode;
  return status !== 401;
}

/**
 * #4386 (AUTHHANG): a session check that got no usable answer in time. Either
 * the server's own deadline answered 503 SESSION_TIMEOUT, or the client gave
 * up waiting on /get-session (`withSessionCheckTimeout` aborts with a
 * TimeoutError). Both mean "sign in again", shown as a banner instead of an
 * endless "Loading...". A present user is never a timeout: an already-loaded
 * session stays on screen.
 */
export function sessionCheckTimedOut(hasUser: boolean, error: unknown): boolean {
  if (hasUser || error == null || typeof error !== "object") return false;
  const { code, name } = error as { code?: unknown; name?: unknown };
  return code === SESSION_TIMEOUT_CODE || name === "TimeoutError";
}

/** How long the client waits on /get-session before giving up (#4386). */
export const SESSION_CHECK_TIMEOUT_MS = 8_000;

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

/**
 * A fetch for the better-auth client that aborts /get-session after
 * `timeoutMs` with a TimeoutError, keeping any caller signal. Every other
 * auth call (sign-in, sign-out) passes through untouched.
 */
export function withSessionCheckTimeout(
  fetchImpl: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> = (input, init) => fetch(input, init),
  timeoutMs: number = SESSION_CHECK_TIMEOUT_MS,
): (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> {
  return (input, init) => {
    if (!/\/get-session(?:[?#]|$)/.test(requestUrl(input))) return fetchImpl(input, init);
    const controller = new AbortController();
    const outer = init?.signal;
    const forward = () => controller.abort(outer?.reason);
    if (outer?.aborted) forward();
    else outer?.addEventListener("abort", forward, { once: true });
    const timer = setTimeout(
      () => controller.abort(new DOMException("Session check timed out", "TimeoutError")),
      timeoutMs,
    );
    return fetchImpl(input, { ...init, signal: controller.signal }).finally(() => {
      clearTimeout(timer);
      outer?.removeEventListener("abort", forward);
    });
  };
}
